/**
 * Dynamic Position Store Persistence Manager
 * 
 * This utility manages user-aware persistence for the position store.
 * It dynamically updates localStorage keys based on user context to prevent
 * cross-user data contamination.
 */

import { getPositionStoreKey, clearAllPositionStoreKeys } from '../store/positionStore';

export interface PositionStoreData {
  positions: Array<[string, any]>;
  isInitialized: boolean;
  totalPositions: number;
  totalUnrealizedPnl: number;
  lastUpdate: number;
  lastSnapshotTime: number | null;
  currentUserId: string | null;
}

/**
 * Save position store data to user-specific localStorage key
 */
export const savePositionStoreData = (userId: string | null, data: PositionStoreData): void => {
  try {
    const key = getPositionStoreKey(userId);
    const dataToSave = {
      ...data,
      currentUserId: userId, // Ensure userId is always saved with the data
      timestamp: Date.now() // Add timestamp for cache validation
    };
    
    localStorage.setItem(key, JSON.stringify(dataToSave));
    console.log(`💾 [Position Store Persistence] Saved data to key: ${key}`);
  } catch (error) {
    console.error('❌ [Position Store Persistence] Failed to save data:', error);
  }
};

/**
 * Load position store data from user-specific localStorage key
 */
export const loadPositionStoreData = (userId: string | null): PositionStoreData | null => {
  try {
    const key = getPositionStoreKey(userId);
    const rawData = localStorage.getItem(key);
    
    if (!rawData) {
      console.log(`📭 [Position Store Persistence] No data found for key: ${key}`);
      return null;
    }
    
    const data = JSON.parse(rawData) as PositionStoreData & { timestamp?: number };
    
    // SECURITY: Validate that the data belongs to the requested user
    if (data.currentUserId !== userId) {
      console.log(`🧹 [Position Store Persistence] Clearing stale user data on login/switch. Previous user: ${data.currentUserId?.substring(0, 20) || 'anonymous'}..., Current user: ${userId?.substring(0, 20)}...`);
      
      // Clear the stale data (expected during user transitions)
      localStorage.removeItem(key);
      return null;
    }
    
    // Validate data age (optional - could expire old cache)
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
    if (data.timestamp && (Date.now() - data.timestamp) > maxAge) {
      console.log(`⏰ [Position Store Persistence] Data expired for key: ${key}`);
      localStorage.removeItem(key);
      return null;
    }
    
    console.log(`💾 [Position Store Persistence] Loaded data from key: ${key}`);
    return data;
  } catch (error) {
    console.error('❌ [Position Store Persistence] Failed to load data:', error);
    return null;
  }
};

/**
 * Switch to a different user's data (handles migration)
 */
export const switchUserContext = (fromUserId: string | null, toUserId: string | null): void => {
  console.log(`🔄 [Position Store Persistence] Switching user context: ${fromUserId?.substring(0, 20)}... → ${toUserId?.substring(0, 20)}...`);
  
  // Note: We don't need to do anything special here since each user has their own localStorage key
  // The position store will automatically load the correct data when setUserContext is called
  
  // Optionally, we could clear the previous user's data for security (but keep it for now)
  // if (fromUserId) {
  //   const fromKey = getPositionStoreKey(fromUserId);
  //   localStorage.removeItem(fromKey);
  // }
};

/**
 * Clear specific user's position data
 */
export const clearUserPositionData = (userId: string | null): void => {
  try {
    const key = getPositionStoreKey(userId);
    localStorage.removeItem(key);
    console.log(`🧹 [Position Store Persistence] Cleared data for user: ${userId?.substring(0, 20)}...`);
  } catch (error) {
    console.error('❌ [Position Store Persistence] Failed to clear user data:', error);
  }
};

/**
 * Get all stored position data keys (for debugging)
 */
export const getAllPositionStoreKeys = (): string[] => {
  try {
    const allKeys = Object.keys(localStorage);
    return allKeys.filter(key => key.startsWith('position-store-persist'));
  } catch (error) {
    console.error('❌ [Position Store Persistence] Failed to get keys:', error);
    return [];
  }
};

/**
 * Validate position store data integrity
 */
export const validatePositionStoreData = (data: PositionStoreData, expectedUserId: string | null): boolean => {
  // Check if data structure is valid
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  // Check if user ID matches
  if (data.currentUserId !== expectedUserId) {
    console.warn(`⚠️ [Position Store Persistence] User ID validation failed. Expected: ${expectedUserId}, Got: ${data.currentUserId}`);
    return false;
  }
  
  // Check if positions array is valid
  if (!Array.isArray(data.positions)) {
    console.warn(`⚠️ [Position Store Persistence] Invalid positions data structure`);
    return false;
  }
  
  return true;
};

/**
 * Emergency cleanup - removes all position store data (for security incidents)
 */
export const emergencyCleanup = (): void => {
  console.log('🚨 [Position Store Persistence] Emergency cleanup initiated');
  clearAllPositionStoreKeys();
  console.log('✅ [Position Store Persistence] Emergency cleanup completed');
};

// Debug utilities for browser console
if (typeof window !== 'undefined') {
  (window as any).debugPositionPersistence = () => {
    const keys = getAllPositionStoreKeys();
    console.table(keys.map(key => ({
      key,
      size: localStorage.getItem(key)?.length || 0,
      data: JSON.parse(localStorage.getItem(key) || '{}')
    })));
    return keys;
  };
  
  (window as any).emergencyCleanupPositions = () => {
    emergencyCleanup();
  };
}